import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import {
  getCampaignPixelHits,
  listUserCampaignsForDebug,
  type PixelHit,
  type PixelHitStatus,
} from "@/lib/campaign-pixel-hits.functions";
import { AlertTriangle, RefreshCw, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/campaign-pixel-hits")({
  component: CampaignPixelHitsPage,
  head: () => ({ meta: [{ title: "Pixel-Hits pro Kampagne — InboxPulse" }] }),
});

const STATUS_LABEL: Record<PixelHitStatus, string> = {
  received: "Empfangen",
  queued: "In Warteschlange",
  processing: "In Verarbeitung",
  dropped: "Verworfen",
};

function CampaignPixelHitsPage() {
  const loadCampaigns = useServerFn(listUserCampaignsForDebug);
  const loadHits = useServerFn(getCampaignPixelHits);
  const [campaignId, setCampaignId] = useState<string>("");

  const campaignsQ = useQuery({
    queryKey: ["campaign-pixel-hits", "campaigns"],
    queryFn: () => loadCampaigns(),
  });

  useEffect(() => {
    if (!campaignId && campaignsQ.data && campaignsQ.data.length > 0) {
      setCampaignId(campaignsQ.data[0].id);
    }
  }, [campaignId, campaignsQ.data]);

  const hitsQ = useQuery({
    queryKey: ["campaign-pixel-hits", "hits", campaignId],
    queryFn: () => loadHits({ data: { campaignId, limit: 100 } }),
    enabled: !!campaignId,
    refetchInterval: 15_000,
  });

  const hits = hitsQ.data?.hits ?? [];
  const counts = hitsQ.data?.counts;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        title="Pixel-Hits pro Kampagne"
        description="Zeigt die letzten Pixel-Hits einer Kampagne mit Event-Status (empfangen, in Warteschlange, in Verarbeitung, verworfen) und ggf. Fehlergrund."
      />

      <HelpPanel
        title="Hilfe: Wer hat meine Mail geöffnet?"
        what={
          <>
            Liste der letzten <b>Öffnungen</b> einer bestimmten Kampagne. Jede Zeile = eine Person
            hat die Mail aufgemacht (oder ihr Mail-Programm hat das Bild geladen).
          </>
        }
        why={
          <>
            Hier siehst du <b>live</b>, wie deine Kampagne läuft. Erste Öffnungen kommen meist nach
            1-10 Minuten.
          </>
        }
        how={[
          <>Kampagne oben auswählen.</>,
          <>Tabelle aktualisiert sich automatisch — neue Treffer erscheinen oben.</>,
          <>
            Status-Spalte verstehen: <b>empfangen</b> = gerade reingekommen, <b>verarbeitet</b> =
            fertig gezählt, <b>verworfen</b> = Duplikat oder ungültig.
          </>,
        ]}
        tips={[
          <>
            Keine Hits nach 30 Min trotz Versand? Pixel ist nicht eingebaut oder Tracking-URL falsch
            — siehe <b>Open-Verification</b>.
          </>,
          <>
            Sehr viele Hits in den ersten 5 Min = Apple Mail lädt vorab, nicht alle haben wirklich
            gelesen.
          </>,
        ]}
        glossary={[
          {
            term: "Hit",
            def: "Ein einzelnes Laden des Tracking-Pixels = jemand hat die Mail (oder das Bild) angefragt.",
          },
          {
            term: "Verworfen",
            def: "Treffer wurde ignoriert — z.B. weil derselbe Empfänger schon gezählt war.",
          },
        ]}
      />

      <Card className="p-4 mb-6 flex flex-col md:flex-row gap-3 md:items-end">
        <div className="flex flex-col gap-1 flex-1">
          <label className="text-xs text-muted-foreground">Kampagne</label>
          <select
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            disabled={campaignsQ.isLoading}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {campaignsQ.isLoading && <option>Lade Kampagnen…</option>}
            {!campaignsQ.isLoading && (campaignsQ.data?.length ?? 0) === 0 && (
              <option value="">Keine Kampagnen</option>
            )}
            {campaignsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => hitsQ.refetch()}
          disabled={!campaignId || hitsQ.isFetching}
          className="h-9 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-3 text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
        >
          <RefreshCw className={cn("size-4", hitsQ.isFetching && "animate-spin")} />
          Aktualisieren
        </button>
      </Card>

      {counts && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Empfangen" value={counts.received} tone="success" />
          <StatCard label="In Warteschlange" value={counts.queued} tone="muted" />
          <StatCard label="In Verarbeitung" value={counts.processing} tone="warn" />
          <StatCard label="Verworfen" value={counts.dropped} tone="danger" />
        </div>
      )}

      {hitsQ.error && (
        <Card className="p-4 mb-6 border-destructive/50 bg-destructive/5 text-destructive flex items-start gap-3">
          <AlertTriangle className="size-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Abfrage fehlgeschlagen</div>
            <div className="text-sm opacity-80 mt-1">
              {hitsQ.error instanceof Error ? hitsQ.error.message : String(hitsQ.error)}
            </div>
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        {campaignId && hitsQ.isLoading && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            Lade Pixel-Hits…
          </div>
        )}
        {campaignId && !hitsQ.isLoading && hits.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            Keine Pixel-Hits für diese Kampagne gefunden.
          </div>
        )}
        {hits.length > 0 && <HitsTable rows={hits} />}
      </Card>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "success" | "muted" | "warn" | "danger";
}) {
  const toneCls = {
    success: "text-emerald-600",
    muted: "text-muted-foreground",
    warn: "text-amber-600",
    danger: "text-destructive",
  }[tone];
  return (
    <Card className="p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("text-2xl font-semibold tabular-nums mt-1", toneCls)}>{value}</div>
    </Card>
  );
}

function StatusBadge({ status }: { status: PixelHitStatus }) {
  const cls =
    status === "received"
      ? "bg-emerald-500/10 text-emerald-600"
      : status === "processing"
        ? "bg-amber-500/10 text-amber-600"
        : status === "dropped"
          ? "bg-destructive/10 text-destructive"
          : "bg-muted text-muted-foreground";
  return (
    <span
      className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium", cls)}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function CopyableId({ value }: { value: string | null }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <button
      onClick={() => navigator.clipboard.writeText(value)}
      title={value}
      className="font-mono text-xs hover:underline inline-flex items-center gap-1 max-w-[180px] truncate"
    >
      <span className="truncate">{value}</span>
      <Copy className="size-3 opacity-50 shrink-0" />
    </button>
  );
}

function HitsTable({ rows }: { rows: PixelHit[] }) {
  function fmtBucket(start: string | null, end: string | null) {
    if (!start || !end) return "—";
    const s = new Date(start);
    const e = new Date(end);
    const sameDay = s.toDateString() === e.toDateString();
    const time = (d: Date) =>
      d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return sameDay
      ? `${s.toLocaleDateString()} ${time(s)}–${time(e)}`
      : `${s.toLocaleString()} – ${e.toLocaleString()}`;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-muted-foreground text-xs">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Zeitpunkt</th>
            <th className="text-left px-3 py-2 font-medium">Status</th>
            <th className="text-left px-3 py-2 font-medium">Event</th>
            <th className="text-left px-3 py-2 font-medium">Provider</th>
            <th className="text-left px-3 py-2 font-medium">Gerät</th>
            <th className="text-left px-3 py-2 font-medium">Land</th>
            <th className="text-left px-3 py-2 font-medium">Versuche</th>
            <th className="text-left px-3 py-2 font-medium">Request ID</th>
            <th className="text-left px-3 py-2 font-medium">Idempotency-Key</th>
            <th className="text-left px-3 py-2 font-medium">5-Min-Bucket</th>
            <th className="text-left px-3 py-2 font-medium">Dedup-Modus</th>
            <th className="text-left px-3 py-2 font-medium">Recipient-Token</th>
            <th className="text-left px-3 py-2 font-medium">Dedup-Grund</th>
            <th className="text-left px-3 py-2 font-medium">Fehlergrund</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.source}-${r.id}`} className="border-t border-border/50">
              <td className="px-3 py-2 text-xs whitespace-nowrap">
                {new Date(r.occurred_at).toLocaleString()}
              </td>
              <td className="px-3 py-2">
                <StatusBadge status={r.status} />
              </td>
              <td className="px-3 py-2 text-xs">{r.event_type}</td>
              <td className="px-3 py-2 text-xs">{r.provider ?? "—"}</td>
              <td className="px-3 py-2 text-xs">{r.device ?? "—"}</td>
              <td className="px-3 py-2 text-xs">{r.country ?? "—"}</td>
              <td className="px-3 py-2 text-xs tabular-nums">{r.attempts ?? "—"}</td>
              <td className="px-3 py-2">
                <CopyableId value={r.request_id} />
              </td>
              <td className="px-3 py-2">
                <CopyableId value={r.idempotency_key} />
              </td>
              <td className="px-3 py-2 text-xs whitespace-nowrap text-muted-foreground">
                {fmtBucket(r.bucket_start, r.bucket_end)}
              </td>
              <td className="px-3 py-2 text-xs whitespace-nowrap">
                <DedupModeCell mode={r.dedup_mode} bucketSeconds={r.dedup_bucket_seconds} />
              </td>
              <td className="px-3 py-2">
                <TokenStatusCell status={r.token_status} token={r.recipient_token} />
              </td>
              <td
                className="px-3 py-2 text-xs text-muted-foreground max-w-[320px]"
                title={r.dedup_reason ?? undefined}
              >
                {r.dedup_reason ?? (r.status === "received" ? "Primär (nicht dedupliziert)" : "—")}
                {r.collided_with_event_id && (
                  <div className="font-mono text-[10px] mt-0.5 opacity-70 truncate">
                    Kollidiert mit Event {r.collided_with_event_id.slice(0, 8)}…
                  </div>
                )}
              </td>
              <td
                className="px-3 py-2 text-xs text-destructive max-w-[280px] truncate"
                title={r.last_error ?? undefined}
              >
                {r.last_error ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DedupModeCell({
  mode,
  bucketSeconds,
}: {
  mode: "total" | "unique" | null;
  bucketSeconds: number | null;
}) {
  if (!mode) return <span className="text-muted-foreground">—</span>;
  if (mode === "unique") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary">
        Unique
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">
        Total
      </span>
      {bucketSeconds ? (
        <span className="text-[10px] text-muted-foreground">{bucketSeconds}s</span>
      ) : null}
    </span>
  );
}

const TOKEN_LABEL: Record<
  NonNullable<PixelHit["token_status"]>,
  { label: string; cls: string; hint: string }
> = {
  valid: {
    label: "OK",
    cls: "bg-emerald-500/10 text-emerald-600",
    hint: "Empfänger-Token korrekt erkannt — Open wird pro Empfänger eindeutig dedupliziert.",
  },
  missing_for_unique: {
    label: "Token fehlt",
    cls: "bg-amber-500/10 text-amber-600",
    hint: "Modus = Unique, aber ?r= wurde nicht übergeben. Fallback auf Provider/Gerät/IP — Mail-Merge-Variable im Versand fehlt.",
  },
  invalid: {
    label: "Ungültig",
    cls: "bg-destructive/10 text-destructive",
    hint: "?r=… enthält ungültige Zeichen oder ist zu lang (max. 128 Zeichen, URL-safe). Wurde verworfen.",
  },
  absent: {
    label: "Kein Token",
    cls: "bg-muted text-muted-foreground",
    hint: "Modus = Total, kein ?r= übergeben — für Total-Mode optional.",
  },
};

function TokenStatusCell({
  status,
  token,
}: {
  status: PixelHit["token_status"];
  token: string | null;
}) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const meta = TOKEN_LABEL[status];
  return (
    <div className="flex flex-col gap-0.5" title={meta.hint}>
      <span
        className={cn(
          "inline-flex w-fit items-center px-1.5 py-0.5 rounded text-[10px] font-medium",
          meta.cls,
        )}
      >
        {meta.label}
      </span>
      {token && (
        <span className="font-mono text-[10px] text-muted-foreground truncate max-w-[160px]">
          {token}
        </span>
      )}
    </div>
  );
}
